"use client";

import { useTranslator } from "~/i18n/provider";
/**
 * P3-11 · Trust & delivery module for the buy box. `template:brandable`: every
 * line, badge, and estimate comes from brand/shipping config — nothing here is
 * hard-coded, so no brand ships a promise it can't keep. Presentational and
 * hook-free (the delivery estimate is computed server-side and passed in), so
 * it renders inside the client buy box. Any unconfigured item is omitted; when
 * nothing is configured the whole module renders nothing.
 */
import { cn } from "~/lib/utils";
import type { DeliveryEstimate } from "~/lib/delivery-estimate";
import type {
  PaymentProvider,
  TrustBadgeIcon,
  TrustConfig,
} from "~/server/api/schemas/brand-settings";

/** Constrained icon set — brand config picks a key, never raw markup. */
const ICON_PATHS: Record<TrustBadgeIcon, string> = {
  shield: "M12 3 4 6v6c0 4.5 3.4 7.3 8 9 4.6-1.7 8-4.5 8-9V6l-8-3Z",
  truck:
    "M3 6h11v9H3zM14 9h4l3 3v3h-7zM7 18a2 2 0 1 0 0 .01M17 18a2 2 0 1 0 0 .01",
  returns: "M3 8h11a5 5 0 0 1 0 10H8M3 8l4-4M3 8l4 4",
  leaf: "M4 20c0-8 6-14 16-14 0 10-6 16-14 16-1 0-2-.5-2-2Z",
  lock: "M6 11h12v9H6zM9 11V8a3 3 0 0 1 6 0v3",
  star: "m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 19l-5.4 2.8 1-6.1L3.2 9.5l6.1-.9L12 3Z",
  clock: "M12 7v5l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z",
  gift: "M4 11h16v9H4zM4 7h16v4H4zM12 7v13M12 7C10 3 6 4 7 7M12 7c2-4 6-3 5 0",
};

function Icon({ name }: { name: TrustBadgeIcon }) {
  return (
    <svg
      aria-hidden
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d={ICON_PATHS[name]} />
    </svg>
  );
}

function Row({
  icon,
  children,
}: {
  icon: TrustBadgeIcon;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-fg-muted">
      <span className="mt-0.5 text-fg">
        <Icon name={icon} />
      </span>
      <span className="min-w-0">{children}</span>
    </li>
  );
}

export interface TrustDeliveryProps {
  /** Server-computed delivery window; null omits the delivery line. */
  delivery: DeliveryEstimate | null;
  /** Brand trust copy + badges. */
  trust: TrustConfig;
  /** Payment lanes currently active in the canonical checkout resolver. */
  paymentMethods: { provider: PaymentProvider; label: string }[];
  className?: string;
}

export function TrustDelivery({
  delivery,
  trust,
  paymentMethods,
  className,
}: TrustDeliveryProps) {
  const t = useTranslator();
  const hasBadges = trust.badges.length > 0;
  const hasPayments = paymentMethods.length > 0;
  const nothing =
    !delivery &&
    !trust.returnsSummary &&
    !trust.warranty &&
    !hasBadges &&
    !trust.secureCheckout &&
    !hasPayments;
  if (nothing) return null;

  return (
    <section
      aria-label={t("pdp.trustSection")}
      data-testid="pdp-trust"
      className={cn(
        "rounded-[var(--radius-lg)] border border-border p-4",
        className,
      )}
    >
      <ul className="flex flex-col gap-3">
        {delivery && (
          <Row icon="truck">
            <span data-testid="pdp-delivery" className="text-fg">
              {delivery.arrivalLabel}
            </span>
            {delivery.dispatchMessage && (
              <span className="mt-0.5 block text-xs text-fg-muted">
                {delivery.dispatchMessage}
              </span>
            )}
          </Row>
        )}
        {trust.returnsSummary && (
          <Row icon="returns">{trust.returnsSummary}</Row>
        )}
        {trust.warranty && <Row icon="shield">{trust.warranty}</Row>}
        {trust.badges.map((badge, i) => (
          <Row key={`${badge.icon}:${i}`} icon={badge.icon}>
            {badge.label}
          </Row>
        ))}
        {trust.secureCheckout && (
          <Row icon="lock">
            <span data-testid="pdp-secure-checkout">
              {t("pdp.secureCheckout")}
            </span>
          </Row>
        )}
      </ul>

      {hasPayments && (
        <ul
          aria-label={t("pdp.acceptedPayments")}
          className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-3"
        >
          {paymentMethods.map((method) => (
            <li
              key={method.provider}
              className="rounded border border-border bg-surface-2 px-2 py-0.5 text-xs font-semibold text-fg-muted"
            >
              {method.provider === "COD" ? "Cash on delivery (where available)" : method.label}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
